import { c as _c } from "react/compiler-runtime";
// Самовывоз от продавца у покупателя (17.10.2026): куда идти, когда открыто,
// как найти, статус выдачи, срок хранения и код получения. Код покупатель
// называет продавцу при выдаче — поэтому он крупно.
import { Clock3, MapPin, Phone, Store } from "lucide-react";
import type { OrderGroup } from "@/api";
import { cn } from "@/lib/utils";
const STATUS: Record<NonNullable<OrderGroup["sellerPickup"]>["status"], string> = {
  new: "Продавец собирает заказ",
  ready: "Готов к выдаче",
  issued: "Выдан",
  expired: "Не забран: срок хранения истёк"
};
const dateTime = (iso?: string) => iso ? new Date(iso).toLocaleString("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
}) : "";
export const isSellerPickupGroup = (g: Pick<OrderGroup, "shipment">) => g.shipment?.carrier === "seller_pickup";
export function SellerPickupCard(t0) {
  const $ = _c(29);
  const {
    group,
    compact,
    className
  } = t0;
  const point = group.shipment?.pickupPoint;
  const view = group.sellerPickup;
  const status = view?.status ?? "new";
  const showCode = Boolean(view?.code) && (status === "new" || status === "ready");
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-3 rounded-[16px] bg-white p-3", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Store className="mt-0.5 size-5 shrink-0 text-[#1F1F1F]" strokeWidth={1.5} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616]">Самовывоз из магазина</p>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = status === "ready" ? "font-medium text-[#32D47D]" : "text-[#1F1F1F]/64";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("mt-1 text-[14px] leading-[18px]", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = STATUS[status];
  const t7 = status === "ready" && view?.storageUntil ? `, хранится до ${dateTime(view.storageUntil)}` : "";
  const t8 = status === "new" && point?.readyLabel ? `, будет готов ${point.readyLabel}` : "";
  let t9;
  if ($[6] !== t5 || $[7] !== t6 || $[8] !== t7 || $[9] !== t8) {
    t9 = <div className="flex min-w-0 items-start gap-2">{t2}<div className="min-w-0">{t3}<p className={t5} data-testid="seller-pickup-status">{t6}{t7}{t8}</p></div></div>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== showCode || $[12] !== view?.code) {
    t10 = showCode && <div className="shrink-0 text-right"><p className="text-[12px] leading-4 text-[#1F1F1F]/48">Код получения</p><p className="text-[22px] leading-7 font-bold tracking-[0.12em] text-[#161616]" data-testid="seller-pickup-code">{view?.code}</p></div>;
    $[11] = showCode;
    $[12] = view?.code;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <div className="flex items-start justify-between gap-3">{t9}{t10}</div>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== compact || $[18] !== point) {
    t12 = point && <div className="flex flex-col gap-1.5 text-[14px] leading-[18px]"><div className="flex items-start gap-2"><MapPin className="mt-0.5 size-4 shrink-0 text-[#1F1F1F]/48" /><p className="min-w-0 text-[#161616]"><span className="font-medium">{point.name}</span><br />{[point.city, point.address].filter(Boolean).join(", ")}</p></div>{point.schedule && <div className="flex items-start gap-2"><Clock3 className="mt-0.5 size-4 shrink-0 text-[#1F1F1F]/48" /><p className="text-[#161616]">{point.schedule}</p></div>}{!compact && point.phone && <div className="flex items-start gap-2"><Phone className="mt-0.5 size-4 shrink-0 text-[#1F1F1F]/48" /><p><a href={`tel:${point.phone.replace(/[^\d+]/g, "")}`} className="text-[#161616] underline-offset-2 hover:underline">{point.phone}</a></p></div>}{!compact && point.instructions && <p className="rounded-[10px] bg-[#F8F8F8] px-3 py-2 text-[13px] leading-[17px] text-[#1F1F1F]/72">{point.instructions}</p>}</div>;
    $[17] = compact;
    $[18] = point;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== compact || $[21] !== point?.storageDays || $[22] !== showCode) {
    t13 = !compact && showCode && <p className="text-[12px] leading-4 text-[#1F1F1F]/48">Назовите код продавцу при получении. Заказ хранится {point?.storageDays ?? 7} дн. после готовности.</p>;
    $[20] = compact;
    $[21] = point?.storageDays;
    $[22] = showCode;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t1 || $[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = <section className={t1} data-testid="seller-pickup-card">{t11}{t12}{t13}</section>;
    $[24] = t1;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}